import React, { Fragment, useEffect, useState } from "react";
import { Alert } from "@nulogy/components";
import Buildkite from "../utils/buildkite";
import { useAuth0 } from "@auth0/auth0-react";
import PropTypes from "prop-types";
import {
  getReadableEnvironment,
  getEnvironmentKey,
} from "../helpers/mappingHelper";
import moment from "moment";
import {
  isFailure,
  isIdle,
  isRunning,
  isSuccessful,
} from "../helpers/pipelineStatusHelper";
import auth_config from "../auth_config.json";

const BuildkiteStatus = ({
  currentPipelineStatus,
  executionStarted,
  executionEndedTime,
}) => {
  const [opsBuilds, setOpsBuilds] = useState([]);
  const [dqiBuilds, setDqiBuilds] = useState([]);
  const buildkite = new Buildkite();
  const { getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    async function fetchData() {
      const token = await getAccessTokenSilently({
        authorizationParams: { audience: auth_config.audience },
      });
      buildkite.getRunningBuilds("opscore-create-demo", token, setOpsBuilds);
      buildkite.getRunningBuilds("qcloud-create-demo", token, setDqiBuilds);
    }

    fetchData();
  }, []);

  const getFinalExecutionTime = () => {
    const ms = moment(executionEndedTime).diff(moment(executionStarted));
    const d = moment.duration(ms);

    return (
      <Fragment>
        {d.hours()}:{d.minutes()}:{d.seconds()}
      </Fragment>
    );
  };

  const renderAlert = () => {
    if (isIdle(currentPipelineStatus)) {
      return <Alert type="informative">No active execution</Alert>;
    } else if (isRunning(currentPipelineStatus)) {
      return (
        <Alert type="warning" data-testid={`running-alert`}>
          {capitalizeStatus()}
          <br />
          Started: {moment(executionStarted).fromNow()}
        </Alert>
      );
    } else if (isFailure(currentPipelineStatus)) {
      return (
        <Alert type="danger" data-testid="failure-alert">
          {capitalizeStatus()} - Time elapsed: {getFinalExecutionTime()}
        </Alert>
      );
    } else if (isSuccessful(currentPipelineStatus)) {
      return (
        <Alert type="success" data-testid="success-alert">
          {capitalizeStatus()} - Time elapsed: {getFinalExecutionTime()}
        </Alert>
      );
    }

    return null;
  };

  const capitalizeStatus = () => {
    return (
      currentPipelineStatus.charAt(0).toUpperCase() +
      currentPipelineStatus.slice(1)
    );
  };

  const renderBuildsForApp = (builds, app) => {
    if (builds.length === 0) {
      return <Alert type="informative">No active demo creations</Alert>;
    }

    return (
      <Fragment>
        {builds.map((build) => {
          return (
            <Alert
              key={build.message}
              type="informative"
              style={{ marginTop: "5px" }}
              data-testid={`build-env-${app}`}
            >
              <p>
                Running on{" "}
                <span style={{ fontWeight: "bold" }}>
                  {getReadableEnvironment(
                    build["meta_data"][getEnvironmentKey(app)],
                  )}{" "}
                </span>
                (Started: {moment(build["created_at"]).fromNow()})
              </p>
              {build.message}
            </Alert>
          );
        })}
      </Fragment>
    );
  };

  const renderBuilds = () => {
    return (
      <Fragment>
        <h6>Shop Floor</h6>
        {renderBuildsForApp(opsBuilds, "ops")}

        <h6 style={{ marginTop: "10px" }}>DQI</h6>

        {renderBuildsForApp(dqiBuilds, "dqi")}
      </Fragment>
    );
  };

  return (
    <div className="card" style={{ marginBottom: "24px" }}>
      <div className="card-body">
        <h3 className="card-title">Buildkite Status</h3>
        <div className="card-text">
          <h4>Current Execution</h4>
          {renderAlert()}

          <h4 style={{ marginTop: "24px" }}>
            Demo Creations currently running
          </h4>
          {renderBuilds()}
        </div>
      </div>
    </div>
  );
};

BuildkiteStatus.propTypes = {
  currentPipelineStatus: PropTypes.string.isRequired,
  executionStarted: PropTypes.number.isRequired,
  executionEndedTime: PropTypes.number.isRequired,
};

export default BuildkiteStatus;
